import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import UserManagement from './UserManagement';
import config from '../config';

function Dashboard({ onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedEnv, setSelectedEnv] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Default to dark mode
    return true;
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [timeRange, setTimeRange] = useState('24h');
  const [customTimeRange, setCustomTimeRange] = useState({
    startTime: '',
    endTime: ''
  });
  const [showCustomTimeModal, setShowCustomTimeModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Frontend cache: avoid re-calling the API for a time range we already have
  const dataCacheRef = React.useRef(new Map());
  const FE_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(prev => {
      const newTheme = !prev;
      localStorage.setItem('theme', newTheme ? 'dark' : 'light');
      return newTheme;
    });
  };

  useEffect(() => {
    fetchCurrentUser();
    fetchDashboardData();
    const interval = setInterval(() => fetchDashboardData(true), 60000); // Refresh every minute (bypass cache)
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, customTimeRange]);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${config.apiUrl}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
      } else {
        // If endpoint doesn't exist yet, extract user from token
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUser({
          id: payload.id,
          username: payload.username,
          role: payload.role || 'viewer'
        });
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      // Fallback: try to decode token
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setCurrentUser({
            id: payload.id,
            username: payload.username,
            role: payload.role || 'viewer'
          });
        }
      } catch (decodeError) {
        console.error('Failed to decode token:', decodeError);
      }
    }
  };

  const fetchDashboardData = async (forceRefresh = false) => {
    // Build query parameters based on time range
    let queryParams = '';
    if (timeRange === 'custom' && customTimeRange.startTime && customTimeRange.endTime) {
      queryParams = `?startTime=${customTimeRange.startTime}&endTime=${customTimeRange.endTime}`;
    } else if (timeRange !== 'custom') {
      queryParams = `?timeRange=${timeRange}`;
    }

    // Serve from frontend cache when fresh (prevents duplicate API calls
    // when switching between filters back and forth)
    const cached = dataCacheRef.current.get(queryParams);
    if (!forceRefresh && cached && (Date.now() - cached.timestamp) < FE_CACHE_DURATION) {
      setData(cached.data);
      setLastUpdated(new Date(cached.timestamp));
      setError(null);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      setRefreshing(true);

      const response = await fetch(`${config.apiUrl}/azure/dashboard${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const dashboardData = await response.json();
        setData(dashboardData);
        setLastUpdated(new Date());
        setError(null);
        dataCacheRef.current.set(queryParams, {
          data: dashboardData,
          timestamp: Date.now()
        });
      } else {
        setError('Failed to fetch dashboard data');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleTimeRangeChange = (value) => {
    if (value === 'custom') {
      setShowCustomTimeModal(true);
    } else {
      setTimeRange(value);
    }
  };

  const handleCustomTimeSubmit = () => {
    if (customTimeRange.startTime && customTimeRange.endTime) {
      setTimeRange('custom');
      setShowCustomTimeModal(false);
      fetchDashboardData();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#ef4444';
      case 'warning': return '#f59e0b';
      case 'info': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      return date.toLocaleDateString();
    } else if (hours > 0) {
      return `${hours}h ago`;
    } else {
      return `${minutes}m ago`;
    }
  };

  return (
    <div className="dashboard">
      {/* Top Banner - Built with OpenAna */}
      <div className="top-banner">
        <span>Built with ❤️ by </span>
        <span className="brand-name">OpenANA</span>
      </div>

      <header className="dashboard-header">
        <div className="header-content">
          <h1>Rwater.com Dashboard</h1>
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          {refreshing && !loading && (
            <span className="refreshing-indicator">
              <span className="spinner-small"></span> Fetching data...
            </span>
          )}
        </div>
        <div className="header-actions">
          {/* Time Range Selector */}
          <div className="time-range-selector">
            <select 
              value={timeRange} 
              onChange={(e) => handleTimeRangeChange(e.target.value)}
              className="time-range-select"
            >
              <option value="1h">Last 1 Hour</option>
              <option value="6h">Last 6 Hours</option>
              <option value="12h">Last 12 Hours</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          {currentUser && currentUser.role === 'admin' && (
            <button onClick={() => setShowUserManagement(true)} className="manage-users-btn">
              👥 Manage Users
            </button>
          )}
          <button onClick={toggleTheme} className="theme-toggle-btn" title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
            {isDarkMode ? '☀️' : '🌙'}
          </button>
          <button onClick={onLogout} className="logout-btn">Logout</button>
        </div>
      </header>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="dashboard-content">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading dashboard data...</p>
          </div>
        </div>
      ) : data ? (
        <div className="dashboard-content">
          {/* Display current time range */}
          {data.timeRange && (
            <div className="time-range-info">
              <span>Showing data from: {new Date(data.timeRange.start).toLocaleString()} to {new Date(data.timeRange.end).toLocaleString()}</span>
            </div>
          )}

          {/* Environments Overview */}
          <section className="environments-section">
            <h2>Environments Overview</h2>
            <div className="cards-grid">
              {data.environments.map((env, index) => (
                <div key={index} className="card environment-card">
                  <div className="card-header">
                    <h3>{env.name}</h3>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(env.health.status) }}
                    >
                      {env.health.status}
                    </span>
                  </div>
                  <div className="card-body">
                    <div className="metric">
                      <span className="metric-label">Current Uptime</span>
                      <span className="metric-value">{env.uptime.percentage}%</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">30-Day Uptime</span>
                      <span className="metric-value">{env.uptime.last30Days}%</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Dashboard Resources</span>
                      <span className="metric-value">{env.health.resourceCount}</span>
                    </div>
                    {env.actualResourceCount && (
                      <div className="metric">
                        <span className="metric-label">Actual Azure Resources</span>
                        <span className="metric-value">{env.actualResourceCount.total}</span>
                      </div>
                    )}
                    <div className="metric">
                      <span className="metric-label">App Services</span>
                      <span className="metric-value">{env.health.appServices}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Active Alerts</span>
                      <span className="metric-value">
                        {env.alerts.filter(a => a.status === 'active').length}
                      </span>
                    </div>
                  </div>
                  {/* Show resource type breakdown if available */}
                  {env.actualResourceCount && env.actualResourceCount.byCategory && (
                    <div className="resource-breakdown-mini">
                      <h4>Resource Types:</h4>
                      {Object.entries(env.actualResourceCount.byCategory).filter(([_, count]) => count > 0).map(([type, count]) => (
                        <div key={type} className="resource-type-item">
                          <span>{type}:</span> <strong>{count}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Alerts & Incidents */}
          <section className="alerts-section">
            <h2>Recent Alerts & Incidents</h2>
            <div className="cards-grid">
              {data.environments.map((env, index) => (
                <div key={index} className="card alerts-card">
                  <h3>{env.name}</h3>
                  {env.alerts.length > 0 ? (
                    <div className="alerts-list">
                      {env.alerts.map((alert, alertIndex) => (
                        <div key={alertIndex} className="alert-item">
                          <div className="alert-header">
                            <span 
                              className="alert-severity"
                              style={{ backgroundColor: getSeverityColor(alert.severity) }}
                            >
                              {alert.severity}
                            </span>
                            <span className="alert-time">{formatTimestamp(alert.timestamp)}</span>
                          </div>
                          <div className="alert-title">{alert.title}</div>
                          <div className="alert-description">{alert.description}</div>
                          <div className="alert-footer">
                            <span className="alert-resource">{alert.resource}</span>
                            <span className={`alert-status ${alert.status}`}>{alert.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-alerts">No recent alerts</div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Uptime Tracking */}
          <section className="uptime-section">
            <h2>Uptime Tracking (Last 30 Days)</h2>
            <div className="cards-grid">
              {data.environments.map((env, index) => (
                <div key={index} className="card uptime-card">
                  <h3>{env.name}</h3>
                  <div className="uptime-stats">
                    <div className="uptime-stat">
                      <span className="stat-label">Current Month</span>
                      <span className="stat-value">{env.uptime.currentMonth}%</span>
                    </div>
                    <div className="uptime-stat">
                      <span className="stat-label">Last 30 Days</span>
                      <span className="stat-value">{env.uptime.last30Days}%</span>
                    </div>
                    <div className="uptime-stat">
                      <span className="stat-label">Last Incident</span>
                      <span className="stat-value small">{formatTimestamp(env.uptime.lastIncident)}</span>
                    </div>
                  </div>
                  <div className="uptime-chart">
                    {env.uptime.history.slice(-30).map((day, dayIndex) => {
                      const uptimeValue = day.uptime;
                      // Green for 99%+, Yellow for 95-99%, Red for <95%
                      const color = uptimeValue >= 99 ? '#10b981' : uptimeValue >= 95 ? '#f59e0b' : '#ef4444';
                      return (
                        <div 
                          key={dayIndex} 
                          className="uptime-bar"
                          style={{ 
                            height: `${uptimeValue}%`,
                            backgroundColor: color
                          }}
                          title={`${day.date}: ${uptimeValue}%`}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Current Metrics */}
          <section className="metrics-section">
            <h2>Current Metrics</h2>
            <div className="cards-grid">
              {data.environments.map((env, index) => (
                <div key={index} className="card metrics-card">
                  <h3>{env.name}</h3>
                  <div className="metrics-grid">
                    <div className="metric-item">
                      <span className="metric-label">CPU Usage</span>
                      <div className="metric-bar">
                        <div 
                          className="metric-bar-fill"
                          style={{ 
                            width: `${env.metrics.cpuUsage}%`,
                            backgroundColor: env.metrics.cpuUsage >= 85 ? '#ef4444' : env.metrics.cpuUsage >= 70 ? '#f59e0b' : '#10b981'
                          }}
                        />
                      </div>
                      <span className="metric-value">{env.metrics.cpuUsage}%</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Memory Usage</span>
                      <div className="metric-bar">
                        <div 
                          className="metric-bar-fill"
                          style={{ 
                            width: `${env.metrics.memoryUsage}%`,
                            backgroundColor: env.metrics.memoryUsage >= 85 ? '#ef4444' : env.metrics.memoryUsage >= 70 ? '#f59e0b' : '#10b981'
                          }}
                        />
                      </div>
                      <span className="metric-value">{env.metrics.memoryUsage}%</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Requests (Last Hour)</span>
                      <span className="metric-value">{env.metrics.requestCount.toLocaleString()}</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Avg Response Time</span>
                      <span className="metric-value">{env.metrics.responseTime}ms</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">HTTP 5xx Errors</span>
                      <span className="metric-value error-value">{env.metrics.httpErrors}</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Data In / Out</span>
                      <span className="metric-value">{env.metrics.dataIn} / {env.metrics.dataOut} MB</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Performance Trends */}
          <section className="trends-section">
            <h2>Performance Trends</h2>
            <div className="env-selector">
              {data.environments.map((env, index) => (
                <button
                  key={index}
                  className={`env-tab ${selectedEnv === index ? 'active' : ''}`}
                  onClick={() => setSelectedEnv(index)}
                >
                  {env.name}
                </button>
              ))}
            </div>
            <div className="trends-grid">
              <div className="card trend-card">
                <h4>CPU Usage Trend</h4>
                <div className="trend-chart">
                  {data.environments[selectedEnv].trends.cpu.map((point, idx) => {
                    const maxValue = Math.max(...data.environments[selectedEnv].trends.cpu.map(p => p.value));
                    const height = (point.value / maxValue) * 100;
                    return (
                      <div key={idx} className="trend-bar-container">
                        <div 
                          className="trend-bar"
                          style={{ height: `${height}%` }}
                          title={`${new Date(point.timestamp).toLocaleTimeString()}: ${point.value}%`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="card trend-card">
                <h4>Memory Usage Trend</h4>
                <div className="trend-chart">
                  {data.environments[selectedEnv].trends.memory.map((point, idx) => {
                    const maxValue = Math.max(...data.environments[selectedEnv].trends.memory.map(p => p.value));
                    const height = (point.value / maxValue) * 100;
                    return (
                      <div key={idx} className="trend-bar-container">
                        <div 
                          className="trend-bar"
                          style={{ height: `${height}%` }}
                          title={`${new Date(point.timestamp).toLocaleTimeString()}: ${point.value}%`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="card trend-card">
                <h4>Request Volume Trend</h4>
                <div className="trend-chart">
                  {data.environments[selectedEnv].trends.requests.map((point, idx) => {
                    const maxValue = Math.max(...data.environments[selectedEnv].trends.requests.map(p => p.value));
                    const height = (point.value / maxValue) * 100;
                    return (
                      <div key={idx} className="trend-bar-container">
                        <div 
                          className="trend-bar"
                          style={{ height: `${height}%` }}
                          title={`${new Date(point.timestamp).toLocaleTimeString()}: ${point.value}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="card trend-card">
                <h4>Response Time Trend</h4>
                <div className="trend-chart">
                  {data.environments[selectedEnv].trends.responseTime.map((point, idx) => {
                    const maxValue = Math.max(...data.environments[selectedEnv].trends.responseTime.map(p => p.value));
                    const height = (point.value / maxValue) * 100;
                    return (
                      <div key={idx} className="trend-bar-container">
                        <div 
                          className="trend-bar"
                          style={{ height: `${height}%` }}
                          title={`${new Date(point.timestamp).toLocaleTimeString()}: ${point.value}ms`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {/* Resource Details */}
          <section className="resources-section">
            <h2>Resource Distribution</h2>
            <div className="cards-grid">
              {data.environments.map((env, index) => (
                <div key={index} className="card resources-card">
                  <h3>{env.name}</h3>
                  <div className="resource-summary">
                    <div className="resource-stat">
                      <span className="stat-label">Total Resources</span>
                      <span className="stat-value">{env.resources.total}</span>
                    </div>
                  </div>
                  <div className="resource-breakdown">
                    <h4>By Type</h4>
                    {Object.entries(env.resources.byType).map(([type, count]) => (
                      <div key={type} className="resource-item">
                        <span className="resource-type">{type}</span>
                        <span className="resource-count">{count}</span>
                      </div>
                    ))}
                  </div>
                  <div className="resource-breakdown">
                    <h4>By Location</h4>
                    {Object.entries(env.resources.byLocation).map(([location, count]) => (
                      <div key={location} className="resource-item">
                        <span className="resource-type">{location}</span>
                        <span className="resource-count">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Cloud Costs */}
          <section className="costs-section">
            <h2>Cloud Costs</h2>
            <div className="costs-grid">
              <div className="card costs-summary-card">
                <div className="costs-header">
                  <div>
                    <h3>{data.costs.period || (data.costs.timeRange ? 
                      `${new Date(data.costs.timeRange.start).toLocaleDateString()} - ${new Date(data.costs.timeRange.end).toLocaleDateString()}` : 
                      data.costs.currentMonth)}</h3>
                    <div className="total-cost">
                      ${data.costs.totalCost.toLocaleString()} <span className="currency">{data.costs.currency}</span>
                    </div>
                  </div>
                </div>
                <div className="costs-breakdown">
                  <h4>Top Services</h4>
                  {data.costs.breakdown.slice(0, 5).map((item, index) => (
                    <div key={index} className="cost-item">
                      <span className="service-name">{item.service}</span>
                      <span className="service-cost">${item.cost}</span>
                    </div>
                  ))}
                </div>
              </div>

              {data.costs.history && data.costs.history.length > 0 && (
                <div className="card costs-history-card">
                  <h4>Cost History</h4>
                  <div className="cost-chart">
                    {data.costs.history.map((item, index) => {
                      const maxCost = Math.max(...data.costs.history.map(h => parseFloat(h.cost)));
                      const height = maxCost > 0 ? (parseFloat(item.cost) / maxCost) * 100 : 0;
                      return (
                        <div key={index} className="chart-bar-container">
                          <div className="chart-bar-wrapper">
                            <div 
                              className="chart-bar"
                              style={{ height: `${height}%` }}
                              title={`${item.label || item.month}: $${item.cost}`}
                            />
                          </div>
                          <span className="chart-label">{item.label || item.month}</span>
                          <span className="chart-value">${item.cost}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {data.costs.breakdown.length > 5 && (
              <div className="card costs-detailed-card">
                <h4>All Services</h4>
                <div className="costs-breakdown-grid">
                  {data.costs.breakdown.map((item, index) => (
                    <div key={index} className="cost-item-detailed">
                      <span className="service-name">{item.service}</span>
                      <span className="service-cost">${item.cost}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      ) : null}

      {showUserManagement && currentUser && (
        <UserManagement 
          currentUser={currentUser}
          onClose={() => setShowUserManagement(false)}
        />
      )}

      {/* Custom Time Range Modal */}
      {showCustomTimeModal && (
        <div className="modal-overlay" onClick={() => setShowCustomTimeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Select Custom Time Range</h3>
            <div className="form-group">
              <label>Start Time:</label>
              <input 
                type="datetime-local" 
                value={customTimeRange.startTime}
                onChange={(e) => setCustomTimeRange({...customTimeRange, startTime: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>End Time:</label>
              <input 
                type="datetime-local" 
                value={customTimeRange.endTime}
                onChange={(e) => setCustomTimeRange({...customTimeRange, endTime: e.target.value})}
              />
            </div>
            <div className="modal-actions">
              <button onClick={handleCustomTimeSubmit} className="btn-primary">Apply</button>
              <button onClick={() => setShowCustomTimeModal(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
